import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pill, Pagination } from '$components'
import { BinIcon } from '$components'
import { ElementIntent } from '$components'
import { formatDate } from '$components'
import { invitationDeleteMutation, invitationIndexOptions, invitationIndexQueryKey } from '$api-client/@tanstack/react-query.gen'
import { useModalState } from '$components'
import { ConfirmationModal } from '$components'
import { GenericTable } from '$components'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import { Invitation } from '$api-client/types.gen'
import { AnimatedOutlet } from '$components'
import { usePage } from '$hooks/use-page'
import { TextCell } from '$components'
import { OptionsCell } from '$components'
import { useTableSort } from '$hooks/use-table-sort'
import { ToastType } from '$components'
import { ToastHelper } from '$components'
import { NoInvitationsEmptyState } from '../components/no-invitations-empty-state'
import { usePageTitle } from '$components'
import { TableSkeleton } from '$components'

const columnHelper = createColumnHelper<Invitation>()

export function InvitationsPage() {
  usePageTitle('Pending Invitations')

  const queryClient = useQueryClient()
  const { modalState, open, close } = useModalState()
  const { page, setPage } = usePage()
  const { getSorting } = useTableSort()

  const { data: invitations, isLoading } = useQuery({
    ...invitationIndexOptions({
      query: {
        page: page,
        sort: getSorting(),
      },
    }),
  })

  const { mutate: revokeInvitation, isPending: isRevoking } = useMutation({
    ...invitationDeleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationIndexQueryKey() })
      ToastHelper.deleteSuccess(ToastType.TeamMemberInvitation)
      close()
    },
    onError: () => ToastHelper.deleteError(ToastType.TeamMemberInvitation),
  })

  const columns = useMemo(() => [
    columnHelper.accessor('email', {
      header: 'Email',
      cell: info => (
        <TextCell>
          { info.getValue() }
        </TextCell>
      ),
    }),
    columnHelper.accessor('inviter.name', {
      header: 'Invited by',
      cell: info => (
        <TextCell>
          { info.getValue() }
        </TextCell>
      ),
    }),
    columnHelper.accessor('expires_at', {
      header: 'Expires at',
      cell: info => (
        <TextCell>
          { formatDate(info.getValue()) }
        </TextCell>
      ),
    }),
    columnHelper.accessor('role', {
      header: 'Role',
      cell: info => (
        <Pill>
          <span className="capitalize">
            {info.getValue()}
          </span>
        </Pill>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      maxSize: 66,
      minSize: 66,
      cell: info => (
        <OptionsCell
          options={[
            {
              id: 'revoke',
              value: 'Revoke',
              intent: ElementIntent.Danger,
              icon: BinIcon,
              onClick: () => open(info.row.original.id),
            },
          ]}
        />
      ),
    }),
  ], [open])

  if (isLoading) {
    return <TableSkeleton
      columns={5}
      rows={10}
    />
  }

  const onConfirmRevoke = () => {
    revokeInvitation({
      path: {
        id: modalState.modelId.toString(),
      },
    })
  }

  const getInvitedUserEmail = () => invitations?.data.find((invitation) => invitation.id === modalState.modelId)?.email

  return (
    <div>
      {invitations?.data.length === 0 && (
        <NoInvitationsEmptyState />
      )}

      {invitations?.data && invitations.data.length > 0 && (
        <GenericTable.Container>
          <GenericTable
            data={invitations.data}
            columns={columns}
          />

          <Pagination
            totalItems={invitations.meta.total}
            currentPage={page}
            totalPages={invitations.meta.last_page}
            onChange={setPage}
          />
        </GenericTable.Container>

      )}

      { modalState.isOpen && (
        <ConfirmationModal
          onClose={close}
          onConfirm={onConfirmRevoke}
          title="Revoke Invitation"
          description={`Are you sure you want to revoke the invitation for ${getInvitedUserEmail()}?`}
          isLoading={isRevoking}
          intent={ElementIntent.Danger}
        />
      )}

      <AnimatedOutlet />
    </div>
  )
}
